// "Diff vA vs vB" lens. Pure presentation of the diff result computed by
// archDiff.js. Lets the user pick A and B via two version selects; defaults
// to (previous, current).

import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Plus, Minus, Pencil, Move } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { diffSnapshots, diffChangeCount } from "../../lib/archDiff";

function SectionHeader({ icon, label, count, color, testid }) {
  return (
    <div
      className="flex items-center gap-2"
      style={{ marginTop: 14, marginBottom: 6 }}
      data-testid={testid}
    >
      <span
        className="inline-flex items-center justify-center"
        style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          background: `color-mix(in oklab, ${color} 14%, transparent)`,
          border: `1px solid color-mix(in oklab, ${color} 45%, transparent)`,
          color,
        }}
      >
        {icon}
      </span>
      <span
        className="font-mono"
        style={{
          fontSize: 10,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--color-text-secondary)",
          fontWeight: 600,
        }}
      >
        {label} · {count}
      </span>
    </div>
  );
}

function Row({ left, right, sub, testid, color }) {
  return (
    <div
      data-testid={testid}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        borderRadius: 8,
        background: "var(--color-background-secondary)",
        border: "1px solid var(--color-border-primary)",
        fontSize: 13,
        marginBottom: 4,
        flexWrap: "wrap",
      }}
    >
      <span style={{ color: color || "var(--color-text-primary)", fontWeight: 500 }}>{left}</span>
      {right && (
        <>
          <ArrowRight size={12} color="var(--color-text-tertiary)" />
          <span style={{ color: "var(--color-text-primary)" }}>{right}</span>
        </>
      )}
      {sub && (
        <span
          className="font-mono"
          style={{ fontSize: 10.5, color: "var(--color-text-tertiary)", marginLeft: "auto" }}
        >
          {sub}
        </span>
      )}
    </div>
  );
}

export default function VersionDiffDialog({ open, onClose, versions }) {
  const [selection, setSelection] = useState(null);

  const sortedVersions = useMemo(
    () => [...new Map((versions || []).map((v) => [v.version, v])).values()]
      .sort((x, y) => x.version - y.version),
    [versions]
  );
  const latest = sortedVersions[sortedVersions.length - 1]?.version ?? null;
  const previous = sortedVersions[sortedVersions.length - 2]?.version ?? null;
  useEffect(() => {
    setSelection(open && previous !== null ? { a: previous, b: latest } : null);
  }, [open, previous, latest]);
  const aVersion = selection?.a ?? previous;
  const bVersion = selection?.b ?? latest;

  const aSnap = useMemo(
    () => sortedVersions.find((v) => v.version === aVersion)?.snapshot,
    [sortedVersions, aVersion]
  );
  const bSnap = useMemo(
    () => sortedVersions.find((v) => v.version === bVersion)?.snapshot,
    [sortedVersions, bVersion]
  );

  const diff = useMemo(() => (aVersion !== bVersion && aSnap && bSnap
    ? diffSnapshots(aSnap, bSnap) : null), [aVersion, bVersion, aSnap, bSnap]);
  const total = diffChangeCount(diff);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="version-diff-dialog"
        className="max-w-[640px] max-h-[88vh] overflow-y-auto"
        style={{
          background: "var(--color-background-primary)",
          border: "1px solid var(--color-border-primary)",
          color: "var(--color-text-primary)",
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ fontSize: 18, fontWeight: 600 }}>
            Version diff
          </DialogTitle>
          <DialogDescription style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            Compare any two saved versions to see what changed between them.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex items-center gap-2 flex-wrap"
          style={{ marginTop: 6 }}
        >
          <VersionSelect
            label="From"
            value={aVersion}
            options={sortedVersions}
            disabledValue={bVersion}
            onChange={(value) => value !== bVersion && setSelection({ a: value, b: bVersion })}
            testid="version-diff-from"
          />
          <ArrowRight size={14} color="var(--color-text-tertiary)" />
          <VersionSelect
            label="To"
            value={bVersion}
            options={sortedVersions}
            disabledValue={aVersion}
            onChange={(value) => value !== aVersion && setSelection({ a: aVersion, b: value })}
            testid="version-diff-to"
          />
          <span
            className="font-mono"
            style={{
              fontSize: 10,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: "4px 10px",
              borderRadius: 9999,
              background: "var(--color-background-secondary)",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-secondary)",
              marginLeft: "auto",
            }}
            data-testid="version-diff-total"
          >
            {total} change{total === 1 ? "" : "s"}
          </span>
        </div>

        {!diff && (
          <p style={{ fontSize: 13, color: "var(--color-text-tertiary)", padding: 12 }}>
            Pick two versions to compare.
          </p>
        )}

        {diff && total === 0 && (
          <p
            style={{
              fontSize: 13,
              color: "var(--color-text-tertiary)",
              padding: 20,
              textAlign: "center",
            }}
            data-testid="version-diff-empty"
          >
            These versions are identical.
          </p>
        )}

        {diff && total > 0 && (
          <div data-testid="version-diff-body">
            {diff.nodesAdded.length > 0 && (
              <>
                <SectionHeader
                  icon={<Plus size={12} />}
                  label="Added"
                  count={diff.nodesAdded.length}
                  color="#22C55E"
                  testid="diff-section-added"
                />
                {diff.nodesAdded.map((n) => (
                  <Row
                    key={n.id}
                    testid={`diff-added-${n.id}`}
                    left={n.name}
                    sub={n.type}
                    color="#22C55E"
                  />
                ))}
              </>
            )}

            {diff.nodesRemoved.length > 0 && (
              <>
                <SectionHeader
                  icon={<Minus size={12} />}
                  label="Removed"
                  count={diff.nodesRemoved.length}
                  color="#EF4444"
                  testid="diff-section-removed"
                />
                {diff.nodesRemoved.map((n) => (
                  <Row
                    key={n.id}
                    testid={`diff-removed-${n.id}`}
                    left={n.name}
                    sub={n.type}
                    color="#EF4444"
                  />
                ))}
              </>
            )}

            {diff.nodesRenamed.length > 0 && (
              <>
                <SectionHeader
                  icon={<Pencil size={12} />}
                  label="Renamed"
                  count={diff.nodesRenamed.length}
                  color="#F59E0B"
                  testid="diff-section-renamed"
                />
                {diff.nodesRenamed.map((n) => (
                  <Row
                    key={n.id}
                    testid={`diff-renamed-${n.id}`}
                    left={n.beforeName}
                    right={n.afterName}
                    sub={n.type}
                  />
                ))}
              </>
            )}

            {diff.nodesMoved.length > 0 && (
              <>
                <SectionHeader
                  icon={<Move size={12} />}
                  label="Moved"
                  count={diff.nodesMoved.length}
                  color="#5B5BD6"
                  testid="diff-section-moved"
                />
                {diff.nodesMoved.map((n) => (
                  <Row
                    key={n.id}
                    testid={`diff-moved-${n.id}`}
                    left={`${n.name} (in ${n.beforeParentName})`}
                    right={`in ${n.afterParentName}`}
                  />
                ))}
              </>
            )}

            {diff.peersAdded.length > 0 && (
              <>
                <SectionHeader
                  icon={<Plus size={12} />}
                  label="Connections added"
                  count={diff.peersAdded.length}
                  color="#22C55E"
                  testid="diff-section-peers-added"
                />
                {diff.peersAdded.map((p) => (
                  <Row
                    key={p.id}
                    testid={`diff-peer-added-${p.id}`}
                    left={`${p.sourceId.slice(0, 8)}…`}
                    right={`${p.targetId.slice(0, 8)}…`}
                    sub={p.label || "connects to"}
                  />
                ))}
              </>
            )}

            {diff.peersRemoved.length > 0 && (
              <>
                <SectionHeader
                  icon={<Minus size={12} />}
                  label="Connections removed"
                  count={diff.peersRemoved.length}
                  color="#EF4444"
                  testid="diff-section-peers-removed"
                />
                {diff.peersRemoved.map((p) => (
                  <Row
                    key={p.id}
                    testid={`diff-peer-removed-${p.id}`}
                    left={`${p.sourceId.slice(0, 8)}…`}
                    right={`${p.targetId.slice(0, 8)}…`}
                    sub={p.label || "connects to"}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function VersionSelect({ label, value, options, disabledValue, onChange, testid }) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        color: "var(--color-text-secondary)",
      }}
    >
      <span
        className="font-mono"
        style={{
          fontSize: 10,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--color-text-tertiary)",
        }}
      >
        {label}
      </span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        style={{
          background: "var(--color-background-secondary)",
          border: "1px solid var(--color-border-primary)",
          color: "var(--color-text-primary)",
          fontSize: 12,
          padding: "4px 8px",
          borderRadius: 6,
          fontFamily: "JetBrains Mono, monospace",
        }}
      >
        {options.length === 0 && <option value="" disabled>No versions</option>}
        {options.map((v) => (
          <option key={v.version} value={v.version} disabled={v.version === disabledValue}>
            {`v${v.version}${v.label ? ` · ${v.label}` : ""}`}
          </option>
        ))}
      </select>
    </label>
  );
}
